import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  message,
  Radio,
  Row,
  Card,
  Divider,
  Progress,
  Typography,
  Carousel,
} from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { MinusOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setupPortal, startPayment } from '../../store/actions/paymentActions';

import { getPlatform, queryString } from '../../utils/helpers';
import { getPrice } from '../../utils/restrictions';

import RootLayout from '../../layout/Layout';
import './style.less';
import { SecondaryNavbar } from '../../components/ActionHeader';
import { deviceWidth } from '../../utils/media-query';
import { useMediaQuery } from 'react-responsive';

import { useCapacitorStripe } from '@capacitor-community/stripe/dist/esm/react/provider';
import { PaymentSheetEventsEnum } from '@capacitor-community/stripe';

const platform = getPlatform();

const plans = ['free', 'lite', 'standard', 'plus'];

const Billing = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();

  const { stripe } = useCapacitorStripe();

  const { me } = useSelector((state) => state.auth);

  const [enabled, setEnabled] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const dispatch = useDispatch();

  useEffect(() => {
    if (queryString(search).status === 'CANCELLED') {
      message.error({
        content: t(
          'billing.payment-cancelled',
          'Payment cancelled. Please try again or contact us if you are facing any issue.',
        ),
        duration: 3,
      });
    } else if (queryString(search).status === 'SUCCESS') {
      message.success({
        content: t('billing.plan-change-successful', 'Plan changed successfully'),
      });
    } else if (queryString(search).status === 'CHANGED') {
      message.success({
        content: t('billing.card-updated-successful', 'Card updated successfully'),
      });
    }
    if (search)
      history.push({
        search: '',
      });
    // TODO Check if the dependencies are required.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dispatch]);

  const pay = async (plan, billingPeriod) => {
    setEnabled(false);
    try {
      const response = await dispatch(startPayment(plan, billingPeriod));
      if (platform === 'web') {
        // Existing web implementation
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('Invalid response from server');
        }
      } else {
        // Capacitor Stripe implementation for non-web platforms
        if (response.data && response.data.paymentIntentClientSecret) {
          await stripe.createPaymentSheet({
            paymentIntentClientSecret: response.data.paymentIntentClientSecret,
            customerEphemeralKeySecret: response.data.customerEphemeralKeySecret,
            customerId: response.data.customerId,
            merchantDisplayName: 'WhatsForm',
          });
          const result = await stripe.presentPaymentSheet();
          if (result.paymentResult === PaymentSheetEventsEnum.Completed) {
            message.success({
              content: t('billing.plan-change-successful', 'Plan changed successfully'),
            });
            window.location.reload();
          } else {
            throw new Error('Payment not completed');
          }
        } else {
          throw new Error('Invalid response from server');
        }
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: t(
          'billing.something-wrong',
          'Something went wrong. Please try again or contact us if you need help.',
        ),
        duration: 3,
      });
    } finally {
      setEnabled(true);
    }
  };

  const isActiveCustomer = me.stripeId && me.plan !== 'free';

  const [selectedPlan, setSelectedPlan] = useState(me.plan === 'free' ? 'standard' : me.plan);

  const isMobile = useMediaQuery({ maxWidth: deviceWidth.mobile });
  const carouselRef = useRef(null);

  useEffect(() => {
    if (carouselRef.current) {
      const selectedIndex = plans.indexOf(selectedPlan);
      const selectedCard = document.getElementById(`plan-${selectedPlan}`);

      if (selectedCard) {
        const containerWidth = carouselRef.current.offsetWidth;
        const cardWidth = selectedCard.offsetWidth;
        const gapWidth = 20; // Match the gap from CSS

        // Calculate scroll position to center the selected card, including gaps
        const scrollPosition =
          (cardWidth + gapWidth) * selectedIndex - containerWidth / 2 + cardWidth / 2;
        carouselRef.current.scrollLeft = Math.max(0, scrollPosition);
      }
    }
  }, [selectedPlan]);

  return (
    <RootLayout showNavbar={!isMobile}>
      <SecondaryNavbar name={t('billing.plans', 'Plans')} />
      <section className="billing-page-section">
        <Card className="current-details">
          <div className="current-plan">
            <div>
              <p style={{ marginBottom: 0 }}>
                {t('common.account')}:{' '}
                <b className="current-plan-name">
                  {me.provider === 'google' ? me.email : `+${me.whatsappNumber}`}
                </b>
              </p>
              <div>
                {t('billing.currentplan', 'Current plan')}:{' '}
                <b className="current-plan-name">{me.plan || 'free'}</b>
                {me.plan === 'pro' && (
                  <p dangerouslySetInnerHTML={{ __html: t('billing.old-pro-message') }}></p>
                )}
              </div>
            </div>
            {isActiveCustomer && (
              <Button
                style={{
                  marginTop: 10,
                }}
                type="primary"
                disabled={!enabled}
                onClick={() => {
                  setEnabled(false);
                  dispatch(setupPortal()).then((response) => {
                    if (response.data && response.data.url)
                      window.location.href = response.data.url;
                    else {
                      message.error({
                        content: t(
                          'billing.something-wrong',
                          'Something went wrong. Please try again or contact us if you need help.',
                        ),
                        duration: 3,
                      });
                      setEnabled(false);
                    }
                  });
                }}
              >
                {t('billing.managepayments')}
              </Button>
            )}
          </div>
        </Card>

        <div className="billing-cycle text-center">
          <Radio.Group onChange={(e) => setBillingCycle(e.target.value)} value={billingCycle}>
            <Radio.Button value="monthly">{t('billing.monthly')}</Radio.Button>
            <Radio.Button value="yearly">{t('billing.yearly')}</Radio.Button>
          </Radio.Group>
          <span className="cycle-discount-arrow"></span>
          <span className="cycle-discount pointer">{t('billing.3-months-free')}</span>
        </div>
        {isMobile ? (
          <div className="pricing-wrapper-mobile">
            <div className="plans-carousel-wrapper" ref={carouselRef}>
              {plans.map((plan) => (
                <Card
                  key={plan}
                  className="plan-card"
                  onClick={() => setSelectedPlan(plan)}
                  id={`plan-${plan}`}
                >
                  <div className="plan-card-name">
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}
                  </div>
                  <div className="plan-card-pricing">
                    {' '}
                    <p className="plan-card-price">${getPrice(plan, billingCycle)}</p>
                  </div>
                  <div>
                    {t('billing.per-month')}
                    <br />
                    {t('billing.billed')} {t('billing.' + billingCycle)}
                  </div>

                  {!isActiveCustomer ? (
                    <Button
                      style={{
                        visibility: plan === 'free' ? 'hidden' : 'visible',
                      }}
                      className="plan-card-button"
                      type="primary"
                      disabled={!enabled}
                      onClick={() => (plan === 'free' ? null : pay(plan, billingCycle))}
                    >
                      {t('billing.pay-with-card')}
                    </Button>
                  ) : (
                    <></>
                  )}
                  <div className="features-row features-row-mobile">
                    {/* Feature rows */}
                    <Row>
                      <Col span={12}>{t('billing.whatsapp-responses')}</Col>
                      <Col span={12}>{t('billing.unlimited')}</Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.form-number')}</Col>
                      <Col span={12}>
                        {plan === 'free' && '2'}
                        {plan === 'lite' && '10'}
                        {plan === 'standard' && '25'}
                        {plan === 'plus' && '50'}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.dashboard-responses')}</Col>
                      <Col span={12}>
                        {plan === 'free' ? <MinusOutlined /> : <i className="fa fa-check" />}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.response-storage')}</Col>
                      <Col span={12}>
                        {plan === 'free' && <MinusOutlined />}
                        {plan === 'lite' && '5000'}
                        {plan === 'standard' && '25000'}
                        {plan === 'plus' && '100000'}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.upload-storage')}</Col>
                      <Col span={12}>
                        {plan === 'free' && <MinusOutlined />}
                        {plan === 'lite' && '1GB'}
                        {plan === 'standard' && '10GB'}
                        {plan === 'plus' && '100GB'}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.seo-meta-data')}</Col>
                      <Col span={12}>
                        {plan === 'free' ? <MinusOutlined /> : <i className="fa fa-check" />}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.custom-link')}</Col>
                      <Col span={12}>
                        {['free', 'lite'].includes(plan) ? (
                          <MinusOutlined />
                        ) : (
                          <i className="fa fa-check" />
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.custom-integrations')}</Col>
                      <Col span={12}>
                        {['free', 'lite'].includes(plan) ? (
                          <MinusOutlined />
                        ) : (
                          <i className="fa fa-check" />
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.removing-branding')}</Col>
                      <Col span={12}>
                        {['free', 'lite'].includes(plan) ? (
                          <MinusOutlined />
                        ) : (
                          <i className="fa fa-check" />
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.advanced-questions')}</Col>
                      <Col span={12}>
                        {['free', 'lite'].includes(plan) ? (
                          <MinusOutlined />
                        ) : (
                          <i className="fa fa-check" />
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={12}>{t('billing.agent-routing')}</Col>
                      <Col span={12}>
                        {['free', 'lite'].includes(plan) ? (
                          <MinusOutlined />
                        ) : (
                          <i className="fa fa-check" />
                        )}
                      </Col>
                    </Row>
                  </div>
                </Card>
              ))}
            </div>

            {/*   <div className="features-row">
              <Row>
                <Col span={12}>{t('billing.whatsapp-responses')}</Col>
                <Col span={12}>{t('billing.unlimited')}</Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.form-number')}</Col>
                <Col span={12}>
                  {selectedPlan === 'free' && '2'}
                  {selectedPlan === 'lite' && '10'}
                  {selectedPlan === 'standard' && '25'}
                  {selectedPlan === 'plus' && '50'}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.dashboard-responses')}</Col>
                <Col span={12}>
                  {selectedPlan === 'free' ? <MinusOutlined /> : <i className="fa fa-check" />}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.response-storage')}</Col>
                <Col span={12}>
                  {selectedPlan === 'free' && <MinusOutlined />}
                  {selectedPlan === 'lite' && '5000'}
                  {selectedPlan === 'standard' && '25000'}
                  {selectedPlan === 'plus' && '100000'}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.upload-storage')}</Col>
                <Col span={12}>
                  {selectedPlan === 'free' && <MinusOutlined />}
                  {selectedPlan === 'lite' && '1GB'}
                  {selectedPlan === 'standard' && '10GB'}
                  {selectedPlan === 'plus' && '100GB'}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.seo-meta-data')}</Col>
                <Col span={12}>
                  {selectedPlan === 'free' ? <MinusOutlined /> : <i className="fa fa-check" />}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.custom-link')}</Col>
                <Col span={12}>
                  {['free', 'lite'].includes(selectedPlan) ? (
                    <MinusOutlined />
                  ) : (
                    <i className="fa fa-check" />
                  )}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.custom-integrations')}</Col>
                <Col span={12}>
                  {['free', 'lite'].includes(selectedPlan) ? (
                    <MinusOutlined />
                  ) : (
                    <i className="fa fa-check" />
                  )}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.removing-branding')}</Col>
                <Col span={12}>
                  {['free', 'lite'].includes(selectedPlan) ? (
                    <MinusOutlined />
                  ) : (
                    <i className="fa fa-check" />
                  )}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.advanced-questions')}</Col>
                <Col span={12}>
                  {['free', 'lite'].includes(selectedPlan) ? (
                    <MinusOutlined />
                  ) : (
                    <i className="fa fa-check" />
                  )}
                </Col>
              </Row>
              <Row>
                <Col span={12}>{t('billing.agent-routing')}</Col>
                <Col span={12}>
                  {['free', 'lite'].includes(selectedPlan) ? (
                    <MinusOutlined />
                  ) : (
                    <i className="fa fa-check" />
                  )}
                </Col>
              </Row>
            </div> */}
          </div>
        ) : (
          <div className="pricing-wrapper">
            <div className="pricing">
              <Row className="pricing-header">
                <Col span={4}></Col>
                <Col span={5}>Free</Col>
                <Col span={5}>Lite</Col>
                <Col span={5}>Standard</Col>
                <Col span={5}>Plus</Col>
              </Row>
              <Row className="pricing-header">
                <Col span={4} className="price"></Col>
                <Col span={5} className="price">
                  ${getPrice('free', billingCycle)}
                </Col>
                <Col span={5} className="price">
                  ${getPrice('lite', billingCycle)}
                </Col>
                <Col span={5} className="price">
                  ${getPrice('standard', billingCycle)}
                </Col>
                <Col span={5} className="price">
                  ${getPrice('plus', billingCycle)}
                </Col>
              </Row>
              <Row className="pricing-header">
                <Col span={4}></Col>
                <Col span={5}>{t('billing.forever')}</Col>
                <Col span={5}>
                  {t('billing.per-month')}
                  <br />
                  {t('billing.billed')} {t('billing.' + billingCycle)}
                </Col>
                <Col span={5}>
                  {t('billing.per-month')} <br />
                  {t('billing.billed')} {t('billing.' + billingCycle)}
                </Col>
                <Col span={5}>
                  {t('billing.per-month')} <br />
                  {t('billing.billed')} {t('billing.' + billingCycle)}
                </Col>
              </Row>

              <Row>
                <Col span={4} className="text-left">
                  {t('billing.whatsapp-responses')}
                </Col>
                <Col span={5}>{t('billing.unlimited')}</Col>
                <Col span={5}>{t('billing.unlimited')}</Col>
                <Col span={5}>{t('billing.unlimited')}</Col>
                <Col span={5}>{t('billing.unlimited')}</Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.form-number')}
                </Col>
                <Col span={5}>2</Col>
                <Col span={5}>10</Col>
                <Col span={5}>25</Col>
                <Col span={5}>50</Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.dashboard-responses')}
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.response-storage')}
                </Col>
                <Col span={5}>
                  {' '}
                  <MinusOutlined />
                </Col>
                <Col span={5}>5000</Col>
                <Col span={5}>25000</Col>
                <Col span={5}>100000</Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.upload-storage')}
                </Col>
                <Col span={5}>
                  {' '}
                  <MinusOutlined />
                </Col>
                <Col span={5}>1GB</Col>
                <Col span={5}>10GB</Col>
                <Col span={5}>100GB</Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.seo-meta-data')}
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.custom-link')}
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.custom-integrations')}
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.removing-branding')}
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
              </Row>

              <Row>
                <Col span={4} className="text-left">
                  {t('billing.advanced-questions')}
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
              </Row>
              <Row>
                <Col span={4} className="text-left">
                  {t('billing.agent-routing')}
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <MinusOutlined />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
                <Col span={5}>
                  <i className="fa fa-check" />
                </Col>
              </Row>

              <Row>
                <Col span={4}></Col>
                <Col span={5}></Col>
                <Col span={5}>
                  {!isActiveCustomer && (
                    <Button
                      type="primary"
                      disabled={!enabled}
                      onClick={() => pay('lite', billingCycle)}
                    >
                      {t('billing.pay-with-card')}
                    </Button>
                  )}
                </Col>
                <Col span={5}>
                  {!isActiveCustomer && (
                    <Button
                      type="primary"
                      disabled={!enabled}
                      onClick={() => pay('standard', billingCycle)}
                    >
                      {t('billing.pay-with-card')}
                    </Button>
                  )}
                </Col>
                <Col span={5}>
                  {!isActiveCustomer && (
                    <Button
                      type="primary"
                      disabled={!enabled}
                      onClick={() => pay('plus', billingCycle)}
                    >
                      {t('billing.pay-with-card')}
                    </Button>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        )}

        <div className="text-center">
          <h4 className="billing-p">
            <img height="25" src="/green-tick.png" alt="tick" />
            <b className="m-l-5">{t('billing.30-day-money-back-policy')}:</b>{' '}
            {t('billing.100-satisfied-with-any-pa')}{' '}
            <a href="https://whatsform.help.center/contact" target="_blank" rel="noreferrer">
              {t('contact-us')}
            </a>{' '}
            {t('billing.to-get-a-full-refund')}
          </h4>
          <div>
            <img width="100" src="/powered-by-stripe.svg" alt="stripe" />
          </div>
          <p className="billing-p">
            {t('billing.earn-commission')}.{' '}
            <a href="https://promote.whatsform.com" target="_blank" rel="noreferrer">
              {t('billing.join-affiliate')}
            </a>
          </p>
          <p className="billing-queries">
            <b>{t('billing.queries')}</b> {t('billing.ask-our-team-at')}{' '}
            <a href="mailto:care@whatsform.com">care@whatsform.com</a>
          </p>
        </div>
      </section>
    </RootLayout>
  );
};

export default Billing;
